import { useState } from 'react';
import { endpoints } from '@/api';
import { errorMessage } from '@/api/errors';
import { useAuth } from '@/stores/auth';
import { useGuidelinesGate } from '@/stores/guidelines';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { GUIDELINES, GUIDELINES_V2_PARAGRAPH } from '@/features/profile/guidelines';

/** Onboarded, but accepted an older guidelines version (SPEC_V4 §7). */
export function needsGuidelines(me: { onboarded: boolean; guidelines_accepted: boolean } | null | undefined): boolean {
  return !!me && me.onboarded && !me.guidelines_accepted;
}

/**
 * Resolve true once the current guidelines are accepted (immediately if they
 * already are). Used before matching, invites and joining a pod.
 */
export function requireGuidelines(): Promise<boolean> {
  if (!needsGuidelines(useAuth.getState().me)) return Promise.resolve(true);
  return useGuidelinesGate.getState().wait();
}

/** Blocking-but-respectful re-accept sheet; hosted once in App. */
export function GuidelinesReacceptHost() {
  const open = useGuidelinesGate((s) => s.open);
  const settle = useGuidelinesGate((s) => s.settle);
  const setMe = useAuth((s) => s.setMe);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      setMe(await endpoints.updateMe({ accept_guidelines: true }));
      settle(true);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={() => settle(false)}
      title="We’ve updated our guidelines"
      description="Please read what’s changed before your next call. It takes a minute."
      testId="guidelines-reaccept"
      maxHeight="92vh"
    >
      <div className="rounded-card bg-raised p-4">
        <p className="flex items-center gap-2 text-[17px] font-semibold">
          <Icon name="shield" size={20} /> New: camera safety
        </p>
        <p className="mt-2 text-[15px] leading-relaxed">{GUIDELINES_V2_PARAGRAPH}</p>
      </div>
      <button type="button" onClick={() => setAll((v) => !v)} className="mt-3 inline-flex h-11 items-center gap-1 text-[15px] font-semibold text-muted hover:text-fg" aria-expanded={all}>
        <Icon name={all ? 'chevron-down' : 'chevron-right'} size={18} /> {all ? 'Hide' : 'Read'} all guidelines
      </button>
      {all && (
        <ul className="mt-1 divide-y divide-line rounded-card bg-raised">
          {GUIDELINES.map((g) => (
            <li key={g.title} className="px-4 py-3">
              <p className="text-[15px] font-semibold">{g.title}</p>
              <p className="mt-0.5 text-[15px] text-muted">{g.body}</p>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-[15px] text-muted">You’ll need to agree before matching, calling friends or joining a Drop. Everything else works as usual.</p>
      {err && (
        <p role="alert" className="mt-3 text-sm font-medium text-danger">
          {err}
        </p>
      )}
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Button variant="outline" accent="paper" onClick={() => settle(false)} disabled={busy} className="order-2 sm:order-1">
          Not now
        </Button>
        <Button onClick={() => accept()} loading={busy} icon="check" className="order-1 sm:order-2" data-testid="guidelines-reaccept-accept">
          I agree
        </Button>
      </div>
    </Sheet>
  );
}
